import { BULK_ORDER_GRID_SIGNS } from './config'

export const formatPowerLabel = value => {
  const formatted = Math.abs(value).toFixed(2)
  if (value > 0) return `+${formatted}`
  if (value < 0) return `-${formatted}`
  return '0.00'
}

export const getQuantityKey = (sphere, cylinder, addition = 0) =>
  `${sphere}|${cylinder}|${addition}`

/** Snap a power value to the nearest grid step (avoids float drift in S+C). */
export const roundPower = (value, step) => {
  if (!step || step <= 0) return value
  return Math.round(value / step) * step
}

/**
 * Legacy ODW minus-cylinder coordinate transform (docs/bulk-old.md §2).
 * Minus view is a display transform of plus-cylinder data, not a separate product set.
 * SphereX = Sphere + Cylinder, CylinderX = -Cylinder
 * Involutive: applying twice returns the original coords.
 */
export const toMinusCylinderCoords = (sphere, cylinder) => {
  const negated = -cylinder
  return {
    sphere: sphere + cylinder,
    cylinder: negated === 0 ? 0 : negated,
  }
}

/** Canonicalize to plus-cylinder coords (cart identity). Already-plus values pass through. */
export const toPlusCylinderCoords = (sphere, cylinder) => {
  if (cylinder >= 0) return { sphere, cylinder }
  return toMinusCylinderCoords(sphere, cylinder)
}

/** Map stored plus coords to the active display notation. */
export const toDisplayPowers = (sphere, cylinder, cylinderSign) => {
  if (cylinderSign !== BULK_ORDER_GRID_SIGNS.MINUS) return { sphere, cylinder }
  return toMinusCylinderCoords(sphere, cylinder)
}

const columnHasAvailability = (available, colIndex) =>
  available.some(row => row[colIndex])

const rowHasAvailability = (available, rowIndex) =>
  available[rowIndex]?.some(cell => cell)

export const trimUnavailableColumns = (columns, available) => {
  if (!columns?.length || !available?.length) {
    return { columns: columns || [], available: available || [] }
  }

  const colCount = columns.length
  let firstCol = 0
  while (firstCol < colCount && !columnHasAvailability(available, firstCol)) {
    firstCol += 1
  }

  let lastCol = colCount - 1
  while (lastCol >= firstCol && !columnHasAvailability(available, lastCol)) {
    lastCol -= 1
  }

  if (firstCol > lastCol) {
    return {
      columns: [],
      available: available.map(() => []),
    }
  }

  return {
    columns: columns.slice(firstCol, lastCol + 1),
    available: available.map(row => row.slice(firstCol, lastCol + 1)),
  }
}

export const trimUnavailableSphereRows = (spheres, available) => {
  if (!spheres?.length || !available?.length) {
    return { spheres: spheres || [], available: available || [] }
  }

  const rowCount = spheres.length
  let firstRow = 0
  while (firstRow < rowCount && !rowHasAvailability(available, firstRow)) {
    firstRow += 1
  }

  let lastRow = rowCount - 1
  while (lastRow >= firstRow && !rowHasAvailability(available, lastRow)) {
    lastRow -= 1
  }

  if (firstRow > lastRow) {
    return {
      spheres: [],
      available: [],
    }
  }

  return {
    spheres: spheres.slice(firstRow, lastRow + 1),
    available: available.slice(firstRow, lastRow + 1),
  }
}

const EMPTY_GRID = { spheres: [], cylinders: [] }

/**
 * Legacy minus-cylinder row/column axes derived from a plus grid.
 * - Column axis: CylinderX = -Cylinder (same column order as plus, no reverse)
 * - Row axis: unique(S+C) over the plus Sphere[] × Cylinder[] cartesian product, DESC
 * Returns empty axes when the plus grid has no available cells.
 */
const getMinusGridAxesFromPlus = plusGrid => {
  const { spheres, cylinders, available, sphereStep } = plusGrid || {}

  if (!spheres?.length || !cylinders?.length) {
    return EMPTY_GRID
  }

  const hasAvailableCell = available?.some(row => row?.some(cell => cell))
  if (!hasAvailableCell) {
    return EMPTY_GRID
  }

  const minusCylinders = cylinders.map(cylinder => {
    const negated = -cylinder
    // Avoid -0; display and index lookups treat 0 and -0 differently in tests.
    return negated === 0 ? 0 : negated
  })
  const sphereXValues = new Set()

  spheres.forEach(sphere => {
    cylinders.forEach(cylinder => {
      sphereXValues.add(roundPower(sphere + cylinder, sphereStep))
    })
  })

  return {
    spheres: [...sphereXValues].sort((a, b) => b - a),
    cylinders: minusCylinders,
  }
}

/**
 * Derive the minus-cylinder grid from a plus grid (legacy ODW display transform).
 * Each available plus cell (S, C) maps to exactly one minus cell (S+C, -C).
 */
export const deriveMinusGridFromPlus = plusGrid => {
  const { sphereStep, cylinderStep } = plusGrid || {}
  const { spheres: minusSpheres, cylinders: minusCylinders } = getMinusGridAxesFromPlus(plusGrid)

  if (!minusSpheres.length || !minusCylinders.length) {
    return {
      sphereStep: sphereStep ?? 0.25,
      cylinderStep: cylinderStep ?? 0.25,
      spheres: [],
      cylinders: [],
      available: [],
    }
  }

  const sphereRowIndex = new Map(minusSpheres.map((sphere, index) => [sphere, index]))
  const cylinderColIndex = new Map(minusCylinders.map((cylinder, index) => [cylinder, index]))

  const available = minusSpheres.map(() => minusCylinders.map(() => false))

  plusGrid.spheres.forEach((sphere, rowIndex) => {
    plusGrid.cylinders.forEach((cylinder, colIndex) => {
      if (!plusGrid.available?.[rowIndex]?.[colIndex]) return

      const { sphere: sphereX, cylinder: cylinderX } = toMinusCylinderCoords(sphere, cylinder)
      const minusRow = sphereRowIndex.get(roundPower(sphereX, sphereStep))
      const minusCol = cylinderColIndex.get(roundPower(cylinderX, cylinderStep))

      if (minusRow != null && minusCol != null) {
        available[minusRow][minusCol] = true
      }
    })
  })

  const trimmed = trimUnavailableSphereRows(minusSpheres, available)

  return {
    sphereStep,
    cylinderStep,
    spheres: trimmed.spheres,
    cylinders: minusCylinders,
    available: trimmed.available,
  }
}
